import React from 'react';
import { Component, Configuration, Position, PuzzleCheckbox, WithTooltip } from "puzzle"
import virtualScroll from "../../VirtualScroll";
import { Keys } from "@progress/kendo-react-common";
import {
  DropDownList,
  DropDownListFilterChangeEvent,
  ListItemProps
} from "@progress/kendo-react-dropdowns";
import {
  CLASSIFIED_FAVORITES,
  DATA_ITEM,
  PROPS,
  REQUEST_PARAMS,
  RESPONSE,
  STATE
} from './Types';
import * as _ from "lodash";
import { PuzzleTooltip as TpTooltip } from "puzzle/components";

const initData: STATE = {
  data: [],
  changedUserData: [],
  pageDataPopulated: [],
  pageNumber: 0,
  requestInProgress: false,
  show: false,
  skip: 0,
  total: 0,
  userNameFilter: ''
};

const impersonateUserAddress = '/switchUser/switchUser?j_username=';

class ImpersonateUserPicker extends Component<PROPS, STATE> {
  gridRef: any;
  searchDelayTimer: any;
  tooltip: any;
  popupClass: string = 'tp-impersonate-popup-window';
  clearInputClassName: string = 'k-i-search';

  static defaultProps: PROPS = {
    pageSize: 20,
    requestActionName: 'getImpersonateGridNextPageData',
  };

  public state: STATE = initData;

  /**
   * Check's if click is inside anchor or current popup.
   * @param event
   */
  checkClickTarget = (event: any): void => {
    if (!document.getElementsByClassName(this.popupClass)[0]?.contains(event.target)) {
      this.removeListenersAndSendData();
    }
  };

  /**
   * Check's if keypress is esc.
   * @param event
   */
  checkKeyDown = (event: KeyboardEvent): void => {
    let {
      keyCode,
      shiftKey
    } = event;
    const {
      down,
      enter,
      esc,
      tab,
      up
    } = Keys;

    if (keyCode === esc) {
      event.preventDefault();
      this.removeListenersAndSendData(this.gridRef.element.children[0]);
    } else if ((keyCode === up || keyCode === down) && _.get(document, 'activeElement.type') === 'checkbox') {
      event.preventDefault();
    } else if (keyCode === enter) {
      let popup = document.getElementsByClassName(this.popupClass)[0];
      if (popup) {
        const selectedLi = popup.getElementsByClassName('tp-impersonate-list-item k-selected')[0];
        const inputInFocus = popup.getElementsByTagName('input')[0];
        if (selectedLi && inputInFocus === _.get(document, 'activeElement')) {
          event.preventDefault();
          (selectedLi as any).click();
        }
      }
    } else if (keyCode === tab) {
      if (_.get(document, 'activeElement.type') === 'checkbox') {
        if (shiftKey) {
          let popup = document.getElementsByClassName(this.popupClass)[0];
          if (popup) {
            popup.getElementsByTagName('input')[0]?.click();
          }
        } else {
          let nextFocus = [...document.getElementsByClassName('tp-sidebar-toolbar-item-button')].find((element: any) => element.getAttribute('tabindex') === '0');
          if (nextFocus) {
            event.preventDefault();
            this.removeListenersAndSendData(nextFocus as HTMLElement);
          }
        }
      } else if (_.get(document, 'activeElement.tagName') === 'INPUT') {
        if (shiftKey) {
          event.preventDefault();
          this.removeListenersAndSendData(this.gridRef.element.children[0]);
        }
      }
    }
  };

  /**
   * Removes unnecessary listeners prepares favorites data for request
   * @param focusEl
   */
  removeListenersAndSendData = (focusEl?: HTMLElement): void => {
    if (focusEl) {
      focusEl.focus()
    }
    document.removeEventListener('click', this.checkClickTarget);
    document.removeEventListener('keydown', this.checkKeyDown);
    let changedData = this.classifyFavorites(this.state.changedUserData);
    this.dispatch('favoriteImpersonatedUserRequest', '', { changedData });
    this.gridRef.state.text = '';
    this.setState({ ...initData });
  };

  /**
   * Classify favorite and unFavorite users
   * @param changedData
   */
  classifyFavorites = (changedData: DATA_ITEM[]): CLASSIFIED_FAVORITES => {
    let changed: CLASSIFIED_FAVORITES = {
      favorite: { usernames: [] },
      unFavorite: { usernames: [] }
    };

    changedData.forEach((item: DATA_ITEM) => {
      item.favorite ? changed.favorite.usernames.push(item.username) : changed.unFavorite.usernames.push(item.username);
      return changed;
    }, changed);

    return changed
  };

  /**
   * Method executes after setting filter state
   */
  filterChangeCallback = (): void => {
    clearTimeout(this.searchDelayTimer);
    (this.searchDelayTimer as any) = setTimeout(this.filterData, 300);
  };

  /**
   * Dispatches ajax request for new data with current filter
   */
  filterData = (): void => {
    const requestParams = this.prepareRequestParams();
    this.dispatch('getImpersonateUsersWithFilter', '', requestParams);
  };

  /**
   * Continue w/ user impersonation after count request has been stopped
   *
   * @param userName
   */
  continueWithImpersonation = (userName: string) => {
    setTimeout(() => {
      const { REST_SERVER } = Configuration.get();
      const { DOMAIN, PROTOCOL, PORT, NAME } = REST_SERVER;
      let aquaUrl;
      if (PORT) {
        aquaUrl = `${PROTOCOL}://${DOMAIN}:${PORT}/${NAME}`;
      } else {
        aquaUrl = `${PROTOCOL}://${DOMAIN}/${NAME}`;
      }
      window.location.href = `${aquaUrl}${impersonateUserAddress}${userName}&callbackUrl=${window.location.href}?Imp=${this.props.userName}`;
    }, 100)
  };

  /**
   * Handles click on userName
   * @param cellProps
   * @param event
   */
  userNameOnClickHandler = (cellProps: DATA_ITEM, event: any): void => {
    if (event.target.type !== 'checkbox') {
      const {
        username,
        userType,
        supplierViewEnabled
      } = cellProps;
      let isVendor = userType === 'VENDOR';
      let allowImpersonate = isVendor ? supplierViewEnabled : true;
      if (allowImpersonate) {
        let changedData = this.classifyFavorites([...this.state.changedUserData]);
        this.dispatch('cancelActivePingingRequest', '', {
          changedData: changedData,
          userName: username
        });
      } else {
        this.dispatch('unableToImpersonateWarning');
      }
    }
  };

  /**
   * Clear filter value on x-icon click/press
   *
   * @param popup
   */
  clearIconOnClick = (popup: HTMLElement) => {
    const input = popup.getElementsByTagName('input')[0];
    if (input) {
      this.gridRef.state.text = '';
      this.setState({
        userNameFilter: ''
      }, this.filterChangeCallback);
    }
  };

  /**
   * Page size getter
   */
  getPageSize = (): number => this.props.pageSize;

  /**
   * Opens popup window
   */
  onOpen = (): void => {
    this.setState({
      show: !this.state.show,
    }, this.onOpenCallback);
  };

  /**
   * Attaches event listeners if popup state is true
   */
  onOpenCallback = (): void => {
    if (this.state.show) {
      this.dispatch('getImpersonateUsersInitialRequest', Position.end);
      document.addEventListener('click', this.checkClickTarget);
      document.addEventListener('keydown', this.checkKeyDown);
      let popup = document.getElementsByClassName(this.popupClass)[0];
      if (popup) {
        const clearIconEl = popup.getElementsByClassName(this.clearInputClassName)[0];
        if (clearIconEl) {
          clearIconEl.setAttribute('tabindex', '0');
          clearIconEl.addEventListener('click', this.clearIconOnClick.bind(this, popup as HTMLElement));
          clearIconEl.addEventListener('keydown', (event: any) => {
            const key = event.keyCode;
            const { enter } = Keys;
            if (key === enter) {
              this.clearIconOnClick(popup as HTMLElement);
            }
          });
        }
      } else {
        this.removeListenersAndSendData()
      }
    }
  };

  /**
   * Handles input field change
   * @param event
   */
  handleChange = (event: DropDownListFilterChangeEvent): void => {
    this.setState({
      userNameFilter: event.filter.value
    }, this.filterChangeCallback);
  };

  /**
   * Makes request params(method is required by virtualScroll hoc)
   * @param pageNumber
   */
  prepareRequestParams = (pageNumber?: number): REQUEST_PARAMS => {
    const { pageSize } = this.props;
    const { userNameFilter } = this.state;
    let requestParams: REQUEST_PARAMS = {
      params: {
        pageSize: pageSize
      }
    };

    Object.assign(requestParams.params,
      userNameFilter ? { nameFilter: userNameFilter } : null,
      pageNumber ? { pageNumber } : null
    );

    return requestParams;
  };

  /**
   * Sets Source files grid data.
   * @param data
   */
  setGridData = (data: RESPONSE): void => {
    this.props.virtualScroll.setGridData(data);
  };

  /**
   * Favorite checkbox change handler
   */
  onFavoriteChange = (cellProps: DATA_ITEM, event: any): void => {
    let currentDataItem = cellProps;
    let changedUserData = [...this.state.changedUserData];

    let arrayIndex = changedUserData.findIndex((currentValue: any) => currentValue.username === currentDataItem.username);
    if (arrayIndex > -1) {
      changedUserData = changedUserData.filter(item => item.username !== changedUserData[arrayIndex].username)
    } else {
      changedUserData = changedUserData.concat({
        ...cellProps,
        favorite: !cellProps.favorite
      });
    }

    this.setState({
      changedUserData
    }, () => {
      if (event.nativeEvent.type === 'keydown') {
        let popup = document.getElementsByClassName(this.popupClass)[0];
        if (popup) {
          popup.getElementsByTagName('input')[0]?.focus();
        }
      }
    });
  };


  /**
   * Reset's SourceFilesDialog state params
   */
  resetGridData = (): void => {
    this.setState({
      data: [],
      pageDataPopulated: [],
      pageNumber: 0,
      skip: 0
    });
  };

  /**
   * Items render
   * @param li
   * @param itemProps
   */
  itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps): React.ReactNode => {
    const dataItem = itemProps.dataItem;
    const selectedItemClass = (li.props.className === "k-list-item k-selected") ? 'tp-impersonate-list-item k-selected' : 'tp-impersonate-list-item';
    let tabIndex = -1;
    if (li.props.className === "k-list-item k-focus" || li.props.className === "k-list-item k-selected") {
      tabIndex = 0;
    }

    let changedData: DATA_ITEM | undefined = _.find(this.state.changedUserData, (item: DATA_ITEM) => item.username === dataItem.username);
    let { favorite } = dataItem;
    if (changedData) {
      favorite = changedData.favorite
    }

    const itemChildren = (
      <span className={selectedItemClass} onClick={this.userNameOnClickHandler.bind(this, dataItem)}>
        <PuzzleCheckbox
          className={`tp-checkbox-favorite ${favorite ? 'qa-fakeClass-checked' : ''}`}
          defaultChecked={favorite}
          aria-label={this.translate('SRL.favorite')}
          onChange={this.onFavoriteChange.bind(this, dataItem)}
          tabIndex={tabIndex}
        />
        {dataItem?.username}
      </span>
    );
    return React.cloneElement(li, { ...li.props }, itemChildren);
  };

  /**
   * Value render
   *
   * @param element
   */
  valueRender = (element: React.ReactElement<HTMLSpanElement>): React.ReactNode => {
    return React.cloneElement(element, { ...element.props }, '');
  };

  renderBody() {
    let {
      data,
      skip,
      total,
      show
    } = this.state;

    const {
      pageSize
    } = this.props;

    return (
      <>
        <WithTooltip context={this} title={this.translate('impersonateLabel')} htmlTag={'span'}>
          <DropDownList
            ref={(el) => this.gridRef = el}
            ariaLabel={this.translate("SRL.account")}
            className={'tp-impersonate-button tp-icon tp-impersonate'}
            data={data.slice(skip, skip + pageSize)}
            filterable={true}
            header={<span className="tp-dropdown-impersonate-label">{this.translate('impersonateLabel')}</span>}
            itemRender={this.itemRender}
            onFilterChange={this.handleChange}
            opened={show}
            valueRender={this.valueRender}
            onPageChange={this.props.virtualScroll.pageChange}
            onOpen={this.onOpen}
            popupSettings={{
              className: this.popupClass
            }}
            title={this.translate('impersonateLabel')}
            virtual={{
              pageSize,
              total,
              skip
            }}
          />
        </WithTooltip>
        <TpTooltip
          anchorElement={'target'}
          ref={(el: any) => {
            if (!this.tooltip) {
              this.tooltip = el;
            }
          }}
          openDelay={1000}
          position="bottom"
        />
      </>
    );
  }
}

export default virtualScroll(ImpersonateUserPicker);
